/**
 * ArtifactRepairQueue — TASK-758.
 *
 * Agent output that the RM cannot open. Two `email_draft` artifacts on the demo account point
 * at nothing: one id is not a UUID (TASK-693 de-identifier corruption), the other is a
 * well-formed UUID whose draft row is gone. Task detail already reported each honestly — but
 * only to whoever opened that one row, so a broken pointer stayed invisible until an RM
 * expanded the task and met a sign-off they could not complete. This makes it a queue rather
 * than a discovery.
 *
 * Book-level per `DASHBOARD-WIDGETIZATION.md`: no `clientId`. "What can't I open?" must never
 * demand a client, and the model never emits an id — the account rides the `X-Account-ID`
 * header `lib/httpClient` injects.
 *
 * Grounding rule (TASK-706): this widget owns its own loading / empty / failure / data-present
 * statement. The router may say it opened this view; it may not say what is in it. Every reason
 * shown is the SERVER's — the client never infers why something cannot be opened, because the
 * client cannot know.
 */

import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, FileWarning } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { taskService } from '../../../services/taskService';
import type { UnresolvableArtifact } from '../../../types/tasks';
import { WidgetContainer } from '../WidgetContainer';
import { WidgetErrorState } from '../../ui/WidgetErrorState';

export interface ArtifactRepairQueueProps {
  /** Optional short label; the widget renders its own title otherwise. */
  title?: string;
}

const TYPE_LABEL: Record<string, string> = {
  email_draft: 'Email draft',
  document: 'Document',
  brief: 'Brief',
  regulated_verdict: 'Regulated verdict',
};

export function ArtifactRepairQueue({ title }: ArtifactRepairQueueProps) {
  const navigate = useNavigate();

  const query = useQuery({
    queryKey: ['artifacts', 'unresolvable'],
    queryFn: () => taskService.fetchUnresolvableArtifacts(),
  });

  const artifacts: UnresolvableArtifact[] = query.data?.artifacts ?? [];

  return (
    <WidgetContainer title={title ?? 'Output that cannot be opened'} source="tasks">
      {query.isLoading ? (
        <div className="py-8 text-center">
          <div className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-[#002B45] border-t-transparent" />
        </div>
      ) : query.isError ? (
        // Never the empty state on a failure. "Everything can be opened" while the check itself
        // failed is the most expensive lie this widget could tell.
        <WidgetErrorState
          title="Couldn't check the agents' output"
          detail="The task service didn't respond, so whether anything is broken is unknown."
          onRetry={() => void query.refetch()}
        />
      ) : artifacts.length === 0 ? (
        <p
          data-testid="artifact-repair-empty"
          className="py-6 text-center text-sm text-muted-foreground"
        >
          Every artifact the agents produced can be opened.
        </p>
      ) : (
        <div className="space-y-2" data-testid="artifact-repair-list">
          <p className="text-xs text-muted-foreground">
            {artifacts.length === 1
              ? '1 piece of agent output cannot be opened.'
              : `${artifacts.length} pieces of agent output cannot be opened.`}{' '}
            Signing these off needs a recorded exception.
          </p>
          {artifacts.map((a) => (
            <div
              key={a.id}
              data-testid="artifact-repair-row"
              className="rounded-md border border-amber-200 bg-amber-50/50 p-2"
            >
              <div className="flex items-start gap-2">
                <FileWarning className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-700" />
                <div className="min-w-0 flex-1">
                  <button
                    onClick={() => navigate(`/tasks?highlight=${a.task_id}`)}
                    className="truncate text-left text-sm font-medium text-foreground hover:underline"
                  >
                    {/* The task title is what the RM recognises; an artifact id is not. */}
                    {a.task_title ?? 'Untitled task'}
                  </button>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
                    <span className="rounded bg-amber-100 px-1.5 py-0.5 text-amber-900">
                      {TYPE_LABEL[a.artifact_type] ?? a.artifact_type}
                    </span>
                    {a.task_status && <span>task is {a.task_status}</span>}
                  </div>
                  {/* The SERVER's reason, verbatim. */}
                  <p className="mt-1 text-[11px] text-amber-900" data-testid="artifact-repair-reason">
                    {a.reason ?? 'This artifact could not be resolved.'}
                  </p>
                </div>
              </div>
            </div>
          ))}
          <p className="flex items-start gap-1 pt-1 text-[11px] text-muted-foreground">
            <AlertTriangle className="mt-0.5 h-3 w-3 flex-shrink-0" />
            <span>
              These pointers cannot be repaired from here — the underlying record is gone.
              Fabricating a replacement would put invented evidence on a client record.
            </span>
          </p>
        </div>
      )}
    </WidgetContainer>
  );
}

export default ArtifactRepairQueue;
